import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Table, Row, Col, Badge, Spinner, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { 
    getSettlementGroups, previewSettlement, generateSettlement, 
    getSettlements, getVehicles, getSettlementTrips, getTrips,
    markSettlementPaid, voidSettlement, uploadSettlementInvoice, getProviders,
    getSettlementAdvances
} from '../api';
import { generateSettlementPDF } from '../utils/SettlementPDF';
import { generateSettlementExcel } from '../utils/SettlementExcel';
import { generateBulkPaymentExcel } from '../utils/BulkPaymentExcel';
import SearchableSelect from './SearchableSelect';
import AdvancesView from './AdvancesView';
import { 
    DollarSign, Calendar, Filter, Users, ChevronDown, ChevronUp, 
    Eye, CheckCircle, Clock, Search, List, CreditCard
} from 'lucide-react';

const SettlementsView = () => {
    // Maestros
    const [settlementGroups, setSettlementGroups] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [providers, setProviders] = useState([]);
    
    // Filtros
    const [startDate, setStartDate] = useState(new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().slice(0, 10)); // 7 días atrás
    const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
    const [statusFilter, setStatusFilter] = useState('pending'); // 'pending' (Pre-liq) | 'generated' (Historial)
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // 'all' | 'paid' | 'unpaid'
    const [searchType, setSearchType] = useState('vehicle'); // 'group' | 'vehicle' | 'provider'
    
    const [selectedGroup, setSelectedGroup] = useState('');
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [selectedProvider, setSelectedProvider] = useState('');
    const [searchSettlementFolio, setSearchSettlementFolio] = useState(''); // Búsqueda por Nº de liquidación
    
    // Resultados
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    
    // UI State
    const [expandedRow, setExpandedRow] = useState(null);
    const [rowTrips, setRowTrips] = useState({}); // { driverId: [trips] }
    const [rowAdvances, setRowAdvances] = useState({}); // { settlementId: [advances] }
    const [loadingTrips, setLoadingTrips] = useState({});
    
    // NUEVOS: Selección para pago
    const [selectedSettlements, setSelectedSettlements] = useState([]);
    const [selectedPendingDrivers, setSelectedPendingDrivers] = useState([]);
    const [isUpdatingPaid, setIsUpdatingPaid] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentInvoices, setPaymentInvoices] = useState({}); // { settlementId: invoiceNumber }
    const [uploadingInvoiceId, setUploadingInvoiceId] = useState(null);

    useEffect(() => {
        loadMasters();
    }, []);

    useEffect(() => {
        if (statusFilter === 'pending') {
            setSelectedPendingDrivers(results.map(r => r.driver_id));
        } else {
            setSelectedPendingDrivers([]);
        }
    }, [results, statusFilter]);

    const loadMasters = async () => {
        try {
            const [groups, vehs, provs] = await Promise.all([getSettlementGroups(), getVehicles({ limit: 1000 }), getProviders()]);
            setSettlementGroups(groups);
            setVehicles(vehs);
            setProviders(provs || []);
        } catch (e) { console.error(e); }
    };

    const handleSearch = async () => {
        setIsLoading(true);
        setResults([]);
        setExpandedRow(null);
        setSelectedSettlements([]);
        setRowTrips({});
        setLoadingTrips({});
        setHasSearched(true);
        
        try {
            if (statusFilter === 'pending') {
                const data = await previewSettlement(
                    startDate, 
                    endDate, 
                    searchType === 'group' ? selectedGroup : null, 
                    searchType === 'vehicle' ? selectedVehicle : null,
                    searchType === 'provider' ? selectedProvider : null
                );
                setResults(data);
            } else {
                const folioClean = searchSettlementFolio.trim().replace(/^M-0*/i, '');
                const folioNum = folioClean !== '' ? parseInt(folioClean, 10) : null;

                const queryParams = {};
                if (folioNum && !isNaN(folioNum)) {
                    queryParams.folio = folioNum;
                } else {
                    if (startDate) queryParams.start_date = startDate;
                    if (endDate) queryParams.end_date = endDate;
                    if (paymentStatusFilter === 'paid') queryParams.is_paid = true;
                    if (paymentStatusFilter === 'unpaid') queryParams.is_paid = false;
                    if (searchType === 'group' && selectedGroup) queryParams.settlement_group_id = selectedGroup;
                    if (searchType === 'vehicle' && selectedVehicle) queryParams.vehicle_id = selectedVehicle;
                    if (searchType === 'provider' && selectedProvider) queryParams.provider_id = selectedProvider;
                }

                const data = await getSettlements(queryParams);
                setResults(Array.isArray(data) ? data : []);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleRow = async (item) => {
        const id = statusFilter === 'pending' ? item.driver_id : item.id;
        if (expandedRow === id) {
            setExpandedRow(null);
            return;
        }

        setExpandedRow(id);
        
        if (!rowTrips[id]) {
            setLoadingTrips(prev => ({ ...prev, [id]: true }));
            try {
                let tripsData = [];
                let advancesData = [];
                if (statusFilter === 'pending') {
                    // Cargar viajes pendientes para este conductor
                    // IMPORTANTE: Usamos end_time_start/end para coincidir con el preview del backend
                    tripsData = await getTrips({
                        driver_id: item.driver_id,
                        status: 'completed,no_show,cancelled_paid',
                        admin_status: 'processed,settled,pending_conciliation',
                        liquidated_status: 'not_liquidated',
                        start_date: startDate + 'T00:00:00',
                        end_date: endDate + 'T23:59:59',
                        limit: 5000
                    });
                } else {
                    // Cargar viajes y anticipos/descuentos de una liquidación ya generada
                    tripsData = await getSettlementTrips(item.id);
                    try {
                        advancesData = await getSettlementAdvances(item.id);
                    } catch (err) {
                        console.warn("Error al cargar anticipos de la liquidación:", err);
                    }
                }
                setRowTrips(prev => ({ ...prev, [id]: tripsData }));
                setRowAdvances(prev => ({ ...prev, [id]: advancesData }));
            } catch (e) {
                console.error("Error cargando detalle:", e);
            } finally {
                setLoadingTrips(prev => ({ ...prev, [id]: false }));
            }
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedSettlements(results.filter(item => !item.is_paid).map(item => item.id));
        } else {
            setSelectedSettlements([]);
        }
    };

    const handleSelectOne = (itemId) => {
        setSelectedSettlements(prev => 
            prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
        );
    };

    const handleMarkAsPaid = () => {
        if (selectedSettlements.length === 0) return;
        setShowPaymentModal(true);
        const initInvoices = {};
        selectedSettlements.forEach(id => {
            initInvoices[id] = '';
        });
        setPaymentInvoices(initInvoices);
    };

    const handleConfirmPayment = async () => {
        setIsUpdatingPaid(true);
        try {
            for (const id of selectedSettlements) {
                const invoiceNumber = paymentInvoices[id];
                await markSettlementPaid(id, invoiceNumber);
            }
            toast.success("Liquidaciones marcadas como pagadas.");
            setSelectedSettlements([]);
            setShowPaymentModal(false);
            handleSearch(); // Recargar datos
        } catch (e) {
            console.error("Error al marcar como pagadas:", e);
            toast.error("Error al marcar liquidaciones como pagadas.");
        } finally {
            setIsUpdatingPaid(false);
        }
    };

    const handleInvoiceUpload = async (settlementId, e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        setUploadingInvoiceId(settlementId);
        try {
            const updatedSettlement = await uploadSettlementInvoice(settlementId, file);
            toast.success(`📸 Factura procesada con éxito! Folio detectado: ${updatedSettlement.invoice_number || 'No detectado'}`);
            setResults(prev => prev.map(item => item.id === settlementId ? { ...item, invoice_number: updatedSettlement.invoice_number, invoice_url: updatedSettlement.invoice_url } : item));
        } catch (error) {
            console.error("Error al subir la factura:", error);
            toast.error("Error al subir y procesar la factura.");
        } finally {
            setUploadingInvoiceId(null);
            e.target.value = '';
        }
    };

    const handleExportBulkPayment = () => {
        if (selectedSettlements.length === 0) return;
        const selectedData = results.filter(r => selectedSettlements.includes(r.id));
        if (selectedData.length === 0) {
            toast.warn("No se encontraron datos para las liquidaciones seleccionadas.");
            return;
        }
        
        const incompleteDrivers = selectedData.filter(s => {
            const d = s.driver || {};
            return !d.account_number || !d.bank_id || !d.account_type;
        });

        if (incompleteDrivers.length > 0) {
            const names = incompleteDrivers.map(s => s.driver_name).join(', ');
            toast.warn(`Advertencia: Los conductores (${names}) tienen datos bancarios incompletos.`);
        }

        try {
            generateBulkPaymentExcel(selectedData);
            toast.success("Plantilla de pago masivo generada con éxito.");
        } catch (e) {
            console.error("Error al exportar pago masivo:", e);
            toast.error("Error al generar la plantilla de pago masivo.");
        }
    };

    const handleGenerate = async (targetDriverId = null) => {
        let total = 0;
        let count = 0;
        let vehicleId = targetDriverId ? null : (searchType === 'vehicle' ? selectedVehicle : null);
        let driverIds = null;
        
        if (targetDriverId) {
            const driverData = results.find(r => r.driver_id === targetDriverId);
            total = driverData.total_amount;
            count = driverData.total_trips;
            // Para liquidar uno solo, usamos su vehicle_id si lo tenemos o simplemente filtramos por conductor en el backend
            // Pero el endpoint actual recibe vehicle_id o group_id. 
            // Implementaremos la lógica de liquidar uno solo pasando su vehículo específico.
            const veh = vehicles.find(v => v.current_driver_id === targetDriverId);
            vehicleId = veh?.id;
            driverIds = [targetDriverId];
        } else {
            const selectedData = results.filter(r => selectedPendingDrivers.includes(r.driver_id));
            if (selectedData.length === 0) {
                alert("No hay conductores seleccionados para liquidar.");
                return;
            }
            total = selectedData.reduce((acc, curr) => acc + curr.total_amount, 0);
            count = selectedData.reduce((acc, curr) => acc + curr.total_trips, 0);
            driverIds = selectedPendingDrivers;
        }
        
        if (!window.confirm(`¿Confirmar generación de liquidación?\n\nTotal a Pagar: $${total.toLocaleString()}\nViajes: ${count}\n\nEsta acción marcará los viajes como LIQUIDADOS.`)) return; 
        
        setIsLoading(true);
        try {
            await generateSettlement(
                startDate, 
                endDate, 
                searchType === 'group' ? selectedGroup : null, 
                vehicleId,
                driverIds,
                searchType === 'provider' ? selectedProvider : null
            );
            handleSearch();
        } catch (e) { 
            alert("Error al generar."); 
        } finally {
            setIsLoading(false);
        }
    };

    const handleVoidSettlement = async (settlementId) => {
        if (!window.confirm("¿Estás seguro de que deseas anular esta liquidación?\n\nEsta acción eliminará el registro, desvinculará los viajes asociados (dejándolos disponibles para volver a liquidar) y eliminará el egreso correspondiente del Flujo de Caja.")) return;
        
        setIsLoading(true);
        try {
            await voidSettlement(settlementId);
            toast.success("Liquidación anulada con éxito y reservas liberadas.");
            handleSearch(); // Recargar datos
        } catch (e) {
            console.error("Error al anular liquidación:", e);
            toast.error("Error al anular la liquidación.");
        } finally {
            setIsLoading(false);
        }
    };

    const downloadDocs = async (settlement, type) => {
        try {
            const trips = await getSettlementTrips(settlement.id);
            let advances = [];
            try {
                advances = await getSettlementAdvances(settlement.id);
            } catch (err) {
                console.warn("Error al cargar anticipos para el documento:", err);
            }
            const sortedTrips = [...trips].sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time));
            if (type === 'pdf') await generateSettlementPDF(settlement, sortedTrips, advances);
            else generateSettlementExcel(settlement, sortedTrips, advances);
        } catch (e) { alert("Error al descargar documento."); }
    };

    const formatCurrency = (val, currency = 'CLP') => {
        const currUpper = String(currency || 'CLP').toUpperCase();
        if (currUpper === 'PEN') {
            return `S/. ${Math.round(val || 0).toLocaleString()}`;
        } else if (currUpper === 'USD') {
            return `US$ ${Math.round(val || 0).toLocaleString()}`;
        }
        return `$${Math.round(val || 0).toLocaleString()}`;
    };

    const getSettleTotals = (item, trips, advances = []) => {
        let totalFacturarChofer = 0;
        let totalEfectivo = 0;
        let totalVenta = 0;
        let totalCostoProv = 0;
        
        const tripsList = Array.isArray(trips) ? trips : [];
        const currency = tripsList.length > 0
            ? (tripsList[0].provider_currency_code || tripsList[0].local_currency_code || item.currency_code || 'CLP')
            : (item.currency_code || 'CLP');

        tripsList.forEach(t => {
            if (!t) return;
            const waitCosto = parseFloat(t.waiting_time_provider_cost) || 0;
            const cBase = (currency !== 'CLP' ? (t.provider_cost_local || 0) : (t.provider_cost_base || 0)) + waitCosto;
            
            let cPago = t.driver_earnings || 0;
            const extraServices = Array.isArray(t.extra_services) ? t.extra_services : [];
            const reimbursable = extraServices.find(s => s && s.id === 'reimbursable_expense');
            if (reimbursable) {
                cPago += parseFloat(reimbursable.cost) || 0;
            }
            
            totalFacturarChofer += cPago;
            totalVenta += parseFloat(t.final_price || t.estimated_price || t.price || 0);
            totalCostoProv += cBase;

            if (t.payment_method?.toLowerCase() === 'cash' || t.payment_method?.toLowerCase() === 'efectivo') {
                totalEfectivo += cBase; 
            }
        });

        const liquido = (item.total_amount !== undefined && item.total_amount !== null)
            ? item.total_amount
            : Math.max(0, totalFacturarChofer - totalEfectivo);

        let anticipos = 0;
        let abonos = 0;
        if (Array.isArray(advances) && advances.length > 0) {
            advances.forEach(a => {
                const amt = parseFloat(a?.amount) || 0;
                if (amt > 0) anticipos += amt;
                else if (amt < 0) abonos += Math.abs(amt);
            });
        } else {
            const netDiff = (totalFacturarChofer - totalEfectivo) - liquido;
            if (netDiff > 0) anticipos = netDiff;
            else if (netDiff < 0) abonos = Math.abs(netDiff);
        }

        return { 
            bruto: totalFacturarChofer, 
            efectivo: totalEfectivo, 
            anticipos: anticipos, 
            abonos: abonos,
            liquido: liquido,
            currency: currency,
            ventaReal: totalVenta,
            costoProveedor: totalCostoProv
        };
    };

    // KPIs Cálculos
    const totalPending = statusFilter === 'pending' ? results.reduce((a, b) => a + b.total_amount, 0) : 0;
    const tripsCount = statusFilter === 'pending' ? results.reduce((a, b) => a + b.total_trips, 0) : 0;

    return (
        <div className="p-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold text-slate-800 mb-0">Centro de Liquidaciones</h2>
                    <p className="text-muted small mb-0">Gestiona los pagos a conductores y dueños de móviles</p>
                </div>
                {statusFilter === 'pending' && results.length > 0 && (
                    <Button variant="success" size="lg" className="fw-bold shadow-sm px-4 d-flex align-items-center gap-2" onClick={() => handleGenerate()}>
                        <CheckCircle size={20} /> Generar Pago Masivo
                    </Button>
                )}
            </div>

            {/* --- KPIs TOP --- */}
            <Row className="mb-4 g-3">
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-4 d-flex align-items-center">
                            <div className="bg-primary bg-opacity-10 p-3 rounded-4 text-primary me-3">
                                <DollarSign size={28} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Total a Liquidar</small>
                                <h3 className="mb-0 fw-bold">{formatCurrency(totalPending)}</h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-4 d-flex align-items-center">
                            <div className="bg-warning bg-opacity-10 p-3 rounded-4 text-warning me-3">
                                <Clock size={28} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Viajes Pendientes</small>
                                <h3 className="mb-0 fw-bold">{tripsCount} <span className="text-muted fs-6 fw-normal">servicios</span></h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <Card.Body className="p-4 d-flex align-items-center">
                            <div className="bg-info bg-opacity-10 p-3 rounded-4 text-info me-3">
                                <Users size={28} />
                            </div>
                            <div>
                                <small className="text-muted fw-bold text-uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Conductores Activos</small>
                                <h3 className="mb-0 fw-bold">{results.length} <span className="text-muted fs-6 fw-normal">en el periodo</span></h3>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* --- PANEL DE FILTROS --- */}
            <Card className="border-0 shadow-sm rounded-4 mb-4">
                <Card.Body className="p-4">
                    <Row className="g-3 align-items-end">
                        <Col md={2}>
                            <Form.Label className="small fw-bold text-slate-600"><Calendar size={14} className="me-1"/> Desde</Form.Label>
                            <Form.Control type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="form-control-lg border-slate-200" disabled={statusFilter === 'generated' && searchSettlementFolio.trim() !== ''} />
                        </Col>
                        <Col md={2}>
                            <Form.Label className="small fw-bold text-slate-600"><Calendar size={14} className="me-1"/> Hasta</Form.Label>
                            <Form.Control type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="form-control-lg border-slate-200" disabled={statusFilter === 'generated' && searchSettlementFolio.trim() !== ''} />
                        </Col>
                        <Col md={statusFilter === 'generated' ? 2 : 3}>
                            <Form.Label className="small fw-bold text-slate-600"><Filter size={14} className="me-1"/> Etapa / Vista</Form.Label>
                            <Form.Select 
                                value={statusFilter} 
                                onChange={e => { setStatusFilter(e.target.value); setResults([]); setSearchSettlementFolio(''); }}
                                className="form-select-lg"
                                style={{ fontWeight: '600' }}
                            >
                                <option value="pending" className="text-warning">⏳ Preliquidacion</option>
                                <option value="generated" className="text-success">✅ Liquidadas</option>
                                <option value="advances" className="text-info">💸 Anticipos</option>
                            </Form.Select>
                        </Col>
                        {statusFilter === 'generated' && (
                            <>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold text-slate-600"><CreditCard size={14} className="me-1"/> Estado Pago</Form.Label>
                                    <Form.Select 
                                        value={paymentStatusFilter} 
                                        onChange={e => setPaymentStatusFilter(e.target.value)}
                                        className="form-select-lg border-slate-200"
                                        style={{ fontWeight: '600' }}
                                    >
                                        <option value="all">Todas</option>
                                        <option value="paid">✅ Pagadas</option>
                                        <option value="unpaid">⏳ Pendientes de Pago</option>
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Label className="small fw-bold text-slate-600">🔍 Nº Liquidación</Form.Label>
                                    <Form.Control
                                        type="number"
                                        placeholder="Ej: 28"
                                        value={searchSettlementFolio}
                                        onChange={e => setSearchSettlementFolio(e.target.value)}
                                        className="form-control-lg border-primary"
                                        style={{ fontWeight: '600' }}
                                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                                    />
                                    {searchSettlementFolio.trim() !== '' && (
                                        <small className="text-primary fw-bold" style={{fontSize: '9px', display: 'block', marginTop: '4px'}}>⚡ Búsqueda directa por folio (ignora fechas)</small>
                                    )}
                                </Col>
                            </>
                        )}
                        <Col md={statusFilter === 'generated' ? 4 : 5} className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <Form.Label className="small fw-bold text-slate-600">Filtrar por</Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Select value={searchType} onChange={e => setSearchType(e.target.value)} className="w-auto border-slate-200">
                                        <option value="group">Grupo</option>
                                        <option value="vehicle">Móvil</option>
                                        <option value="provider">🏢 Proveedor</option>
                                    </Form.Select>
                                    {searchType === 'group' ? (
                                        <div className="flex-grow-1">
                                            <SearchableSelect 
                                                options={settlementGroups} 
                                                value={selectedGroup} 
                                                onChange={val => setSelectedGroup(val)} 
                                                placeholder="Todos los Grupos..."
                                            />
                                        </div>
                                    ) : searchType === 'provider' ? (
                                        <div className="flex-grow-1">
                                            <SearchableSelect 
                                                options={providers.map(p => ({
                                                    id: p.id,
                                                    name: p.name || p.company_name || `Proveedor #${p.id}`
                                                }))} 
                                                value={selectedProvider} 
                                                onChange={val => setSelectedProvider(val)} 
                                                placeholder="Todos los Proveedores..."
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex-grow-1">
                                            <SearchableSelect 
                                                options={vehicles.map(v => ({
                                                    id: v.id,
                                                    name: `${v.internal_code} - ${v.current_driver?.full_name || 'Sin Conductor'}`,
                                                    internal_code: v.internal_code,
                                                    full_name: v.current_driver?.full_name,
                                                    plate: v.plate
                                                }))} 
                                                value={selectedVehicle} 
                                                onChange={val => setSelectedVehicle(val)} 
                                                placeholder="Todos los Móviles..."
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Button variant="primary" className="fw-bold px-4" onClick={handleSearch} disabled={isLoading} style={{ height: '48px', marginTop: 'auto' }}>
                                {isLoading ? <Spinner size="sm" /> : <Search size={18} />}
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* --- LISTADO DE RESULTADOS --- */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <Card.Header className="bg-white p-3 border-0 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <List size={18} className="text-primary" />
                        <span className="fw-bold text-slate-700">
                            {statusFilter === 'pending' ? 'Detalle de Producción a Liquidar' : 'Historial de Liquidaciones Generadas'}
                        </span>
                    </div>
                    {statusFilter === 'generated' && selectedSettlements.length > 0 && (
                        <div className="d-flex align-items-center gap-2">
                            <Badge bg="primary" className="p-2 fs-6 me-2">
                                Total Seleccionado: {formatCurrency(results.filter(r => selectedSettlements.includes(r.id)).reduce((acc, curr) => acc + curr.total_amount, 0))}
                            </Badge>
                            <Button variant="outline-primary" size="sm" onClick={handleExportBulkPayment} className="me-1">
                                💸 Pago Masivo (Excel)
                            </Button>
                            <Button variant="success" size="sm" onClick={handleMarkAsPaid} disabled={isUpdatingPaid}>
                                {isUpdatingPaid ? <Spinner size="sm" /> : 'Marcar como Pagadas'}
                            </Button>
                        </div>
                    )}
                </Card.Header>
                <Card.Body className="p-0">
                    {statusFilter === 'advances' ? (
                        <AdvancesView vehicles={vehicles} />
                    ) : !hasSearched && results.length === 0 ? (
                        <div className="text-center py-5">
                            <div className="bg-slate-50 d-inline-block p-4 rounded-circle mb-3">
                                <Search size={48} className="text-slate-300" />
                            </div>
                            <h5 className="text-slate-400">Selecciona filtros para ver datos</h5>
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0 align-middle">
                            <thead className="bg-slate-50 border-bottom">
                                {statusFilter === 'pending' ? (
                                    <tr>
                                        <th style={{ width: '40px' }}></th>
                                        <th style={{ width: '40px' }} className="text-center">
                                            <Form.Check 
                                                type="checkbox" 
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedPendingDrivers(results.map(r => r.driver_id));
                                                    } else {
                                                        setSelectedPendingDrivers([]);
                                                    }
                                                }}
                                                checked={results.length > 0 && selectedPendingDrivers.length === results.length} 
                                                disabled={results.length === 0}
                                            />
                                        </th>
                                        <th className="py-3 px-4 text-slate-500 small text-uppercase fw-bold">Conductor / Móvil</th>
                                        <th className="py-3 text-center text-slate-500 small text-uppercase fw-bold">Reservas</th>
                                        <th className="py-3 text-end text-slate-500 small text-uppercase fw-bold">Monto</th>
                                        <th className="py-3 text-slate-500 small text-uppercase fw-bold">Notas</th>
                                        <th className="py-3 text-center text-slate-500 small text-uppercase fw-bold">Estado</th>
                                        <th className="py-3 text-end text-slate-500 small text-uppercase fw-bold pe-4">Acción</th>
                                    </tr>
                                ) : (
                                    <tr>
                                        <th style={{ width: '40px' }}></th>
                                        <th style={{ width: '40px' }} className="text-center">
                                            <Form.Check 
                                                type="checkbox" 
                                                onChange={handleSelectAll} 
                                                checked={results.length > 0 && selectedSettlements.length === results.filter(item => !item.is_paid).length} 
                                                disabled={results.filter(item => !item.is_paid).length === 0}
                                            />
                                        </th>
                                        <th className="py-3 px-4 text-slate-500 small text-uppercase fw-bold">Folio</th>
                                        <th className="py-3 text-slate-500 small text-uppercase fw-bold">Fecha</th>
                                        <th className="py-3 text-slate-500 small text-uppercase fw-bold">Conductor</th>
                                        <th className="py-3 text-center text-slate-500 small text-uppercase fw-bold">Viajes</th>
                                        <th className="py-3 text-end text-slate-500 small text-uppercase fw-bold">Total</th>
                                        <th className="py-3 text-slate-500 small text-uppercase fw-bold">Notas</th>
                                        <th className="py-3 text-center text-slate-500 small text-uppercase fw-bold pe-4">Acciones</th>
                                    </tr>
                                )}
                            </thead>
                            <tbody className="border-0">
                                {results.length === 0 ? (
                                    <tr><td colSpan="9" className="text-center py-5 text-muted">No hay liquidaciones en este periodo.</td></tr>
                                ) : (
                                    results.map((item, idx) => {
                                        const itemId = statusFilter === 'pending' ? item.driver_id : item.id;
                                        const isExpanded = expandedRow === itemId;

                                        return (
                                            <React.Fragment key={itemId}>
                                                <tr style={{ transition: 'all 0.2s' }} className={isExpanded ? 'bg-primary bg-opacity-5' : ''}>
                                                    <td className="ps-3 text-center" onClick={() => toggleRow(item)} style={{ cursor: 'pointer' }}>
                                                        {isExpanded ? <ChevronUp size={18} className="text-primary" /> : <ChevronDown size={18} className="text-slate-400" />}
                                                    </td>
                                                    {statusFilter === 'pending' ? (
                                                        <>
                                                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                                <Form.Check 
                                                                    type="checkbox" 
                                                                    checked={selectedPendingDrivers.includes(item.driver_id)} 
                                                                    onChange={() => {
                                                                        setSelectedPendingDrivers(prev => {
                                                                            if (prev.includes(item.driver_id)) {
                                                                                return prev.filter(id => id !== item.driver_id);
                                                                            } else {
                                                                                return [...prev, item.driver_id];
                                                                            }
                                                                        });
                                                                    }}
                                                                />
                                                            </td>
                                                            <td className="px-4 fw-bold text-slate-700">
                                                                {item.driver_name} 
                                                                <Badge bg="info" className="ms-2 bg-opacity-10 text-info border border-info border-opacity-25">Móvil {item.vehicle_code}</Badge>
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge bg="light" text="dark" className="px-2 py-1 border">{item.total_trips} reservas</Badge>
                                                            </td>
                                                            <td className="text-end fw-bold text-success fs-5">{formatCurrency(item.total_amount, item.currency_code)}</td>
                                                            <td>
                                                                {item.notes && (() => {
                                                                    const isPureCredit = item.notes.includes('Abonados') && !item.notes.includes('Descontados');
                                                                    return (
                                                                        <Badge
                                                                            bg={isPureCredit ? 'success' : 'warning'}
                                                                            text={isPureCredit ? undefined : 'dark'}
                                                                            className={`bg-opacity-10 text-${isPureCredit ? 'success' : 'warning'} border border-${isPureCredit ? 'success' : 'warning'} border-opacity-25`}
                                                                            title={item.advance_details ? item.advance_details.join('\n') : ''}
                                                                        >
                                                                            {item.notes}
                                                                        </Badge>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge bg="warning" className="px-2 py-1 fw-normal text-dark bg-opacity-25" style={{ color: '#856404' }}>PENDIENTE</Badge>
                                                            </td>
                                                            <td className="text-end pe-4">
                                                                <div className="d-flex justify-content-end gap-2">
                                                                    <Button variant="outline-primary" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1" onClick={(e) => { e.stopPropagation(); toggleRow(item); }}>
                                                                        <Eye size={14} /> Detalle
                                                                    </Button>
                                                                    <Button variant="success" size="sm" className="fw-bold px-3 d-flex align-items-center gap-1 shadow-sm" onClick={(e) => { e.stopPropagation(); handleGenerate(item.driver_id); }}>
                                                                        <CreditCard size={14} /> Liquidar
                                                                    </Button>
                                                                </div>
                                                            </td>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                                                <Form.Check 
                                                                    type="checkbox" 
                                                                    checked={selectedSettlements.includes(item.id)} 
                                                                    onChange={() => handleSelectOne(item.id)} 
                                                                    disabled={item.is_paid}
                                                                />
                                                            </td>
                                                            <td className="px-4 fw-bold text-primary">M-{String(item.id).padStart(3, '0')}</td>
                                                            <td className="small text-muted">{new Date(item.created_at).toLocaleDateString()}</td>
                                                            <td className="fw-bold">
                                                                {item.driver?.full_name}
                                                                {item.is_paid && (
                                                                    <>
                                                                        <Badge bg="success" className="ms-2 bg-opacity-10 text-success border border-success border-opacity-25">PAGADO</Badge>
                                                                        {item.invoice_number && (
                                                                            <span className="ms-2 text-muted small fw-normal font-monospace">
                                                                                Factura: {item.invoice_number}
                                                                                {item.invoice_url && (
                                                                                    <a href={item.invoice_url} target="_blank" rel="noopener noreferrer" className="ms-1 text-decoration-none fw-bold text-primary">
                                                                                        ↗
                                                                                    </a>
                                                                                )}
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td className="text-center">{item.total_trips}</td>
                                                            <td className="text-end fw-bold text-slate-800">{formatCurrency(item.total_amount, item.currency_code)}</td>
                                                            <td>
                                                                {item.notes && (() => {
                                                                    const isPureCredit = item.notes.includes('Abonados') && !item.notes.includes('Descontados');
                                                                    return (
                                                                        <Badge
                                                                            bg={isPureCredit ? 'success' : 'warning'}
                                                                            text={isPureCredit ? undefined : 'dark'}
                                                                            className={`bg-opacity-10 text-${isPureCredit ? 'success' : 'warning'} border border-${isPureCredit ? 'success' : 'warning'} border-opacity-25`}
                                                                            title={item.advance_details ? item.advance_details.join('\n') : ''}
                                                                        >
                                                                            {item.notes}
                                                                        </Badge>
                                                                    );
                                                                })()}
                                                            </td>
                                                            <td className="text-center pe-4">
                                                                <div className="d-flex justify-content-center gap-2">
                                                                    <Button variant="outline-danger" size="sm" className="rounded-circle p-2" onClick={(e) => { e.stopPropagation(); downloadDocs(item, 'pdf'); }} title="PDF">📄</Button>
                                                                    <Button variant="outline-success" size="sm" className="rounded-circle p-2" onClick={(e) => { e.stopPropagation(); downloadDocs(item, 'excel'); }} title="Excel">📊</Button>
                                                                    {item.invoice_url ? (
                                                                         <Button variant="primary" size="sm" className="rounded-circle p-2" onClick={(e) => { e.stopPropagation(); window.open(item.invoice_url, '_blank'); }} title={`Ver Factura #${item.invoice_number}`}>🧾</Button>
                                                                     ) : (
                                                                         <div className="d-inline-block" onClick={(e) => e.stopPropagation()}>
                                                                             <input 
                                                                                 type="file" 
                                                                                 id={`admin-invoice-upload-${item.id}`} 
                                                                                 style={{ display: 'none' }} 
                                                                                 accept="image/*,application/pdf" 
                                                                                 onChange={(e) => handleInvoiceUpload(item.id, e)} 
                                                                             />
                                                                             {uploadingInvoiceId === item.id ? (
                                                                                 <Button variant="outline-primary" size="sm" className="rounded-circle p-2" disabled>
                                                                                     <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '1.5px' }} />
                                                                                 </Button>
                                                                             ) : (
                                                                                 <Button 
                                                                                     variant="outline-primary" 
                                                                                     size="sm" 
                                                                                     className="rounded-circle p-2" 
                                                                                     onClick={() => document.getElementById(`admin-invoice-upload-${item.id}`).click()} 
                                                                                     title="Subir Factura (SII)"
                                                                                 >
                                                                                     📤
                                                                                 </Button>
                                                                             )}
                                                                         </div>
                                                                     )}
                                                                    <Button variant="outline-danger" size="sm" className="rounded-circle p-2" onClick={(e) => { e.stopPropagation(); handleVoidSettlement(item.id); }} title="Anular Liquidación">🗑️</Button>
                                                                </div>
                                                            </td>
                                                        </>
                                                    )}
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="10" className="p-0 border-0">
                                                            <div className="bg-light p-4 shadow-inner border-bottom border-top">
                                                                {rowTrips[itemId] && !loadingTrips[itemId] && (() => {
                                                                     try {
                                                                         const totals = getSettleTotals(item, rowTrips[itemId], rowAdvances[itemId]);
                                                                         return (
                                                                             <div className="mb-4 bg-white p-3 rounded-3 border shadow-sm">
                                                                                 <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                                                                     <h6 className="fw-bold mb-0 text-slate-700 small text-uppercase" style={{ letterSpacing: '0.5px' }}>Resumen Liquidación Financiera</h6>
                                                                                     <div className="d-flex align-items-center gap-2">
                                                                                         {item.is_paid ? (
                                                                                             <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25">PAGADA</Badge>
                                                                                         ) : (
                                                                                             <Badge bg="warning" className="bg-opacity-10 text-warning border border-warning border-opacity-25 text-dark">PENDIENTE</Badge>
                                                                                         )}
                                                                                         {item.invoice_number && (
                                                                                             <div className="d-flex align-items-center gap-2">
                                                                                                 <Badge bg="secondary" className="bg-opacity-10 text-secondary border border-secondary border-opacity-25 font-monospace">Factura: {item.invoice_number}</Badge>
                                                                                                 {item.invoice_url && (
                                                                                                     <a href={item.invoice_url} target="_blank" rel="noopener noreferrer" className="btn btn-link btn-sm p-0 text-decoration-none fw-bold text-primary" style={{ fontSize: '11px' }}>
                                                                                                         Ver Factura ↗
                                                                                                     </a>
                                                                                                 )}
                                                                                             </div>
                                                                                         )}
                                                                                     </div>
                                                                                 </div>
                                                                                 <Row className="g-2 text-center">
                                                                                     <Col xs={6} md>
                                                                                         <div className="p-2 border rounded bg-light bg-opacity-50">
                                                                                             <small className="text-muted fw-bold text-uppercase d-block" style={{ fontSize: '9px' }}>Total Producción</small>
                                                                                             <span className="fw-bold text-slate-800" style={{ fontSize: '15px' }}>{formatCurrency(totals.ventaReal, totals.currency)}</span>
                                                                                         </div>
                                                                                     </Col>
                                                                                     <Col xs={6} md>
                                                                                         <div className="p-2 border rounded bg-light bg-opacity-50">
                                                                                             <small className="text-muted fw-bold text-uppercase d-block" style={{ fontSize: '9px' }}>Costo Proveedor</small>
                                                                                             <span className="fw-bold text-slate-800" style={{ fontSize: '15px' }}>{formatCurrency(totals.costoProveedor, totals.currency)}</span>
                                                                                         </div>
                                                                                     </Col>
                                                                                     <Col xs={6} md>
                                                                                         <div className="p-2 border rounded bg-light bg-opacity-50">
                                                                                             <small className="text-muted fw-bold text-uppercase d-block" style={{ fontSize: '9px' }}>Cobrado Efectivo (Cash)</small>
                                                                                             <span className="fw-bold text-slate-600" style={{ fontSize: '15px' }}>{totals.efectivo > 0 ? `-${formatCurrency(totals.efectivo, totals.currency)}` : (totals.currency === 'PEN' ? 'S/. 0' : (totals.currency === 'USD' ? 'US$ 0' : '$0'))}</span>
                                                                                         </div>
                                                                                     </Col>
                                                                                     <Col xs={6} md>
                                                                                         <div className="p-2 border rounded bg-light bg-opacity-50">
                                                                                             <small className="text-muted fw-bold text-uppercase d-block" style={{ fontSize: '9px' }}>Anticipos / Descuentos</small>
                                                                                             {totals.anticipos > 0 ? (
                                                                                                 <span className="fw-bold text-danger" style={{ fontSize: '15px' }}>
                                                                                                     -{formatCurrency(totals.anticipos, totals.currency)}
                                                                                                 </span>
                                                                                             ) : (
                                                                                                 <span className="text-muted small" style={{ fontSize: '14px' }}>{totals.currency === 'PEN' ? 'S/. 0' : (totals.currency === 'USD' ? 'US$ 0' : '$0')}</span>
                                                                                             )}
                                                                                         </div>
                                                                                     </Col>
                                                                                     {totals.abonos > 0 && (
                                                                                         <Col xs={6} md>
                                                                                             <div className="p-2 border border-success border-opacity-25 rounded bg-success bg-opacity-10">
                                                                                                 <small className="text-success fw-bold text-uppercase d-block" style={{ fontSize: '9px' }}>Abonos / Reintegros</small>
                                                                                                 <span className="fw-bold text-success" style={{ fontSize: '15px' }}>
                                                                                                     +{formatCurrency(totals.abonos, totals.currency)}
                                                                                                 </span>
                                                                                             </div>
                                                                                         </Col>
                                                                                     )}
                                                                                     <Col xs={6} md>
                                                                                         <div className="p-2 border border-success border-opacity-25 rounded bg-success bg-opacity-10">
                                                                                             <small className="text-success fw-bold text-uppercase d-block" style={{ fontSize: '9px' }}>Líquido a Pagar</small>
                                                                                             <span className="fw-bold text-success" style={{ fontSize: '16px' }}>{formatCurrency(totals.liquido, totals.currency)}</span>
                                                                                         </div>
                                                                                     </Col>
                                                                                 </Row>
                                                                             </div>
                                                                         );
                                                                     } catch (error) {
                                                                         console.error("Error al calcular totales:", error);
                                                                         return (
                                                                             <div className="alert alert-danger small py-2 mb-3">
                                                                                 ⚠️ Error al calcular totales financieros: {error.message}
                                                                             </div>
                                                                         );
                                                                     }
                                                                 })()}

                                                                 {/* Vista previa de ajustes pendientes antes de liquidar */}
                                                                 {statusFilter === 'pending' && Array.isArray(item.advance_details) && item.advance_details.length > 0 && (
                                                                     <div className="mb-4 bg-white p-3 rounded-3 border shadow-sm">
                                                                         <h6 className="fw-bold mb-2 text-slate-700 small text-uppercase" style={{ letterSpacing: '0.5px' }}>
                                                                             ⚖️ Ajustes Pendientes a Aplicar al Liquidar
                                                                         </h6>
                                                                         <div className="d-flex flex-column gap-1 small">
                                                                             {item.advance_details.map((detailLine, idx) => {
                                                                                 const isCreditLine = String(detailLine).trim().startsWith('+');
                                                                                 return (
                                                                                     <div
                                                                                         key={idx}
                                                                                         className={`px-2 py-1 rounded border ${isCreditLine ? 'bg-success bg-opacity-10 border-success border-opacity-25 text-success fw-bold' : 'bg-danger bg-opacity-10 border-danger border-opacity-25 text-danger fw-bold'}`}
                                                                                     >
                                                                                         {isCreditLine ? '🏆 ' : '🔻 '}{detailLine}
                                                                                     </div>
                                                                                 );
                                                                             })}
                                                                         </div>
                                                                     </div>
                                                                 )}

                                                                 {/* Descuentos, Penalizaciones y Abonos */}
                                                                 {rowAdvances[itemId] && rowAdvances[itemId].length > 0 && (() => {
                                                                     const hasCredits = rowAdvances[itemId].some(a => (parseFloat(a.amount) || 0) < 0);
                                                                     const hasDebits = rowAdvances[itemId].some(a => (parseFloat(a.amount) || 0) > 0);
                                                                     const themeColor = hasCredits && !hasDebits ? 'success' : (hasCredits && hasDebits ? 'primary' : 'danger');
                                                                     const sectionTitle = hasCredits && !hasDebits
                                                                         ? '🏆 Abonos y Reintegros Aplicados en esta Liquidación'
                                                                         : (hasCredits && hasDebits
                                                                             ? '⚖️ Ajustes Aplicados (Descuentos y Abonos)'
                                                                             : '🔻 Descuentos y Penalizaciones Aplicadas en esta Liquidación');
                                                                     return (
                                                                         <div className={`mb-4 bg-${themeColor} bg-opacity-10 p-3 rounded-3 border border-${themeColor} border-opacity-25`}>
                                                                             <h6 className={`fw-bold mb-2 text-${themeColor} small text-uppercase`} style={{ letterSpacing: '0.5px' }}>
                                                                                 {sectionTitle}
                                                                             </h6>
                                                                             <Table size="sm" responsive className="bg-white mb-0 rounded-2 border">
                                                                                 <thead className={`bg-${themeColor} bg-opacity-10 text-${themeColor} small fw-bold`}>
                                                                                     <tr>
                                                                                         <th className="ps-3">Fecha</th>
                                                                                         <th>Tipo / Concepto</th>
                                                                                         <th>Detalle y Referencia (OS / Viaje)</th>
                                                                                         <th className="text-end pe-3">Monto Ajuste</th>
                                                                                     </tr>
                                                                                 </thead>
                                                                                 <tbody className="small">
                                                                                     {rowAdvances[itemId].map(adv => {
                                                                                         const rawAmt = parseFloat(adv.amount) || 0;
                                                                                         const isCredit = rawAmt < 0;
                                                                                         const notesStr = adv.notes || (isCredit ? "Abono / Reintegro Booking" : "Anticipo de caja");
                                                                                         const isPenalty = !isCredit && (notesStr.toLowerCase().includes('disputa') || notesStr.toLowerCase().includes('booking') || notesStr.toLowerCase().includes('penalizaci'));
                                                                                         return (
                                                                                             <tr key={adv.id}>
                                                                                                 <td className="ps-3 text-muted">{adv.created_at ? new Date(adv.created_at).toLocaleDateString('es-CL') : 'N/A'}</td>
                                                                                                 <td className={`fw-bold ${isCredit ? 'text-success' : 'text-danger'}`}>
                                                                                                     {isCredit ? '🏆 Abono / Reintegro Booking' : (isPenalty ? '⚠️ Penalización Booking' : '💵 Anticipo de Caja')}
                                                                                                 </td>
                                                                                                 <td>{notesStr}</td>
                                                                                                 <td className={`text-end fw-bold pe-3 ${isCredit ? 'text-success' : 'text-danger'}`}>
                                                                                                     {isCredit
                                                                                                         ? `+${formatCurrency(Math.abs(rawAmt), item.currency_code || 'CLP')}`
                                                                                                         : `-${formatCurrency(rawAmt, item.currency_code || 'CLP')}`}
                                                                                                 </td>
                                                                                             </tr>
                                                                                         );
                                                                                     })}
                                                                                 </tbody>
                                                                             </Table>
                                                                         </div>
                                                                     );
                                                                 })()}

                                                                 <h6 className="fw-bold mb-3 d-flex align-items-center gap-2">
                                                                     <Search size={16} className="text-primary" /> Viajes realizados por {statusFilter === 'pending' ? item.driver_name : (item.driver?.full_name || 'Conductor')} en el periodo
                                                                 </h6>
                                                                 {loadingTrips[itemId] ? (
                                                                     <div className="text-center py-3"><Spinner animation="border" variant="primary" size="sm" /> Cargando...</div>
                                                                 ) : (
                                                                     (() => {
                                                                         try {
                                                                             const tripsToRender = (rowTrips[itemId] || []).filter(t => t);
                                                                             return (
                                                                                 <div className="rounded-3 overflow-hidden border">
                                                                                     <Table size="sm" className="bg-white mb-0">
                                                                                         <thead className="bg-slate-200 small">
                                                                                             <tr>
                                                                                                 <th className="ps-3">ID</th>
                                                                                                 <th>Fecha</th>
                                                                                                 <th>Cliente</th>
                                                                                                 <th>Pasajero</th>
                                                                                                 <th>Estado</th>
                                                                                                 <th>Origen / Destino</th>
                                                                                                 <th className="text-end">Costo Prov.</th>
                                                                                                 <th className="text-end pe-3">Monto Pago</th>
                                                                                             </tr>
                                                                                         </thead>
                                                                                         <tbody className="small">
                                                                                             {tripsToRender.map(t => (
                                                                                                 <tr key={t.id}>
                                                                                                     <td className="ps-3 fw-bold text-muted">#{t.id}</td>
                                                                                                     <td>
                                                                                                         {(() => {
                                                                                                             try {
                                                                                                                 const date = new Date(t.scheduled_time);
                                                                                                                 if (isNaN(date.getTime())) {
                                                                                                                     return t.scheduled_time || 'Sin fecha';
                                                                                                                 }
                                                                                                                 return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
                                                                                                             } catch (e) {
                                                                                                                 return 'Fecha inválida';
                                                                                                             }
                                                                                                         })()}
                                                                                                     </td>
                                                                                                     <td className="fw-bold text-primary">{t.client?.name || 'Particular'}</td>
                                                                                                     <td className="fw-bold">{t.passenger_name || 'Sin Pasajero'}</td>
                                                                                                     <td>
                                                                                                         {t.status === 'no_show' ? (
                                                                                                             <Badge bg="danger" className="bg-opacity-10 text-danger border border-danger border-opacity-25" style={{fontSize: '10px'}}>NO SHOW</Badge>
                                                                                                         ) : t.status === 'cancelled_paid' ? (
                                                                                                             <Badge bg="info" className="bg-opacity-10 text-info border border-info border-opacity-25" style={{fontSize: '10px'}}>CANCELADO C/CARGO</Badge>
                                                                                                         ) : (
                                                                                                             <Badge bg="success" className="bg-opacity-10 text-success border border-success border-opacity-25" style={{fontSize: '10px'}}>COMPLETADO</Badge>
                                                                                                         )}
                                                                                                     </td>
                                                                                                     <td className="text-truncate" style={{ maxWidth: '250px' }}>
                                                                                                         <span className="text-danger">A:</span> {t.pickup_address ? t.pickup_address.substring(0,30) : ''}... <br/>
                                                                                                         <span className="text-primary">B:</span> {t.dropoff_address ? t.dropoff_address.substring(0,30) : ''}...
                                                                                                     </td>
                                                                                                     <td className="text-end text-muted">
                                                                                                         {formatCurrency(
                                                                                                             (t.provider_currency_code || t.local_currency_code || 'CLP') !== 'CLP' 
                                                                                                                 ? (t.provider_cost_local || 0) 
                                                                                                                 : (t.provider_cost_base || 0), 
                                                                                                             t.provider_currency_code || t.local_currency_code
                                                                                                         )}
                                                                                                     </td>
                                                                                                     <td className="text-end fw-bold text-success pe-3">
                                                                                                         {formatCurrency(t.driver_earnings || 0, t.provider_currency_code || t.local_currency_code)}
                                                                                                     </td>
                                                                                                 </tr>
                                                                                             ))}
                                                                                             {tripsToRender.length === 0 && (
                                                                                                 <tr><td colSpan="8" className="text-center py-3">No hay viajes registrados.</td></tr>
                                                                                             )}
                                                                                         </tbody>
                                                                                     </Table>
                                                                                 </div>
                                                                             );
                                                                         } catch (error) {
                                                                             console.error("Error al renderizar lista de viajes:", error);
                                                                             return (
                                                                                 <div className="alert alert-danger small py-2">
                                                                                     ⚠️ Error al renderizar tabla de viajes: {error.message}
                                                                                 </div>
                                                                             );
                                                                         }
                                                                     })()
                                                                 )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
            {/* Modal de Facturación */}
            <Modal show={showPaymentModal} onHide={() => setShowPaymentModal(false)} size="lg" centered>
                <Modal.Header closeButton className="bg-slate-50">
                    <Modal.Title className="fw-bold text-slate-700">Ingresar Facturas para Pago</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">
                        Por cada liquidación seleccionada, ingresa el número de factura correspondiente si aplica.
                    </p>
                    <Table hover responsive className="align-middle">
                        <thead className="bg-slate-50">
                            <tr>
                                <th>Liquidación</th>
                                <th>Conductor</th>
                                <th className="text-end">Monto</th>
                                <th>Nº Factura / Boleta</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedSettlements.map(id => {
                                const s = results.find(r => r.id === id);
                                return (
                                    <tr key={id}>
                                        <td className="fw-bold text-primary">M-{String(id).padStart(3, '0')}</td>
                                        <td>{s?.driver?.full_name || 'N/A'}</td>
                                        <td className="text-end fw-bold">{formatCurrency(s?.total_amount || 0)}</td>
                                        <td>
                                            <Form.Control 
                                                type="text" 
                                                placeholder="Ej: 12345" 
                                                value={paymentInvoices[id] || ''} 
                                                onChange={e => setPaymentInvoices({
                                                    ...paymentInvoices,
                                                    [id]: e.target.value
                                                })}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </Modal.Body>
                <Modal.Footer className="bg-slate-50">
                    <Button variant="outline-secondary" onClick={() => setShowPaymentModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="success" onClick={handleConfirmPayment} disabled={isUpdatingPaid}>
                        {isUpdatingPaid ? <Spinner size="sm" /> : "Confirmar Pagos"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SettlementsView;
